import { toast } from 'react-toastify';

import { createDefaultHeaders } from '@/api';
import { getSession, urlWithAdminQuery } from '@/auth';
import { log } from '@/util';

import {
  IAccontInfo,
  IAccountWithUserAccounts,
  IAddUserAccountRequest,
  IAddUserAccountResponse,
  IBodyCreateAccountLink,
  IBodyUpdateUserInfo,
  ICreateAccountLinkResponse,
  IFeatures,
  IListRolesResponse,
  IMyPermissionsResponse,
  ISetMemberRoleRequest,
  IUpdateUserAccountNoteResponse,
  IUserAccountBase,
  UserAccountUpdateRequest,
} from './types';

export const getJSONOrThrow = (resp: Response) => {
  if (!resp.ok || resp.status < 200 || resp.status >= 400) {
    return resp.text().then((text) => {
      throw new Error(text);
    });
  }

  return resp.json();
};

/**
 * Get User Info from backend
 *
 * @returns {Promise} - Result of call to get user info
 */
export const getUserInfo = (): Promise<IAccontInfo> => {
  return getSession().then((session) => {
    let ep = `/user/${session.idToken.payload.sub || ''}`;
    if (session.adminEmail !== '') ep = '/user/getByAdminEmail';
    return fetch(urlWithAdminQuery(ep, session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const newErr = new Error(`Unable to load user information: ${err.message}`);
        toast.error(
          `Failed to retrieve your account details. Please refresh the page or contact support if the issue persists.`,
          { closeButton: true },
        );
        log.error(newErr);
        throw newErr;
      });
  });
};

/**
 * Get Stripe feature Info from backend
 *
 * @returns {Promise} - Result of call to get user info
 */
export const getStripeFeature = (): Promise<IFeatures> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery('/feature', session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const newErr = new Error(`Error loading features: ${err.message}`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
        throw newErr;
      }),
  );
};

/**
 * Update User Info from backend
 *
 * @returns {Promise} - Result of call to Update user info
 */
export const updateUserInfo = (body: IBodyUpdateUserInfo, idUser: string): Promise<IAccontInfo> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/user/${idUser || ''}`, session.adminEmail), {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const errorMessage = err.message || '';
        let userMessage = '';

        if (errorMessage.includes('Only owners can modify other owners')) {
          userMessage = 'Only owners can modify other owners.';
        } else if (errorMessage.includes('Only owners')) {
          userMessage = 'Only owners can perform this action.';
        } else {
          userMessage = `Error updating user: ${errorMessage}`;
        }

        const newErr = new Error(userMessage);
        log.error(newErr);
        throw newErr;
      }),
  );
};

/**
 * Create a stripe connected account for the logged in user
 *
 * @returns {Promise} - Result of call to Update user info
 */
export const createStripeAccount = (): Promise<IAccontInfo> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery('/stripe/account', session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const newErr = new Error(`Error creating Stripe account: ${err.message}`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

/**
 * Create a stripe connected account for the logged in user
 *
 * @returns {Promise} - Result of call to Update user info
 */
export const createStripeAccountLink = (
  body: IBodyCreateAccountLink,
): Promise<ICreateAccountLinkResponse> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery('/stripe/accountLink', session.adminEmail), {
      method: 'POST',
      body: JSON.stringify(body),
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const newErr = new Error(`Error creating Stripe account link: ${err.message}`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

/**
 * LIST all User Accounts on this Account
 */
export const listAccounts = (): Promise<IAccountWithUserAccounts[]> =>
  getSession().then((session) =>
    fetch(urlWithAdminQuery('/account', session.adminEmail), {
      method: 'GET',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const e = new Error(`Error listing accounts: ${err.message}`);
        toast.error(
          `Unable to load account information. Please refresh the page or contact support if the issue continues.`,
          { closeButton: true },
        );
        log.error(e);
      }),
  );

/**
 * Invite a new User to this Account
 */
export const addUserAccount = (body: IAddUserAccountRequest): Promise<IAddUserAccountResponse> =>
  getSession().then((session) =>
    fetch(urlWithAdminQuery('/account/addUserAccount', session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
      body: JSON.stringify(body),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        // Parse backend error messages and create user-friendly error
        const errorMessage = err.message || '';
        let userMessage = '';

        if (errorMessage.includes('Missing required permissions')) {
          userMessage = 'You do not have permission to invite team members.';
        } else if (errorMessage.includes('already exists')) {
          userMessage = 'This email address is already associated with an account.';
        } else if (errorMessage.includes('Only owners can invite other owners')) {
          userMessage =
            'Only owners can invite other owners. Please select a different role or contact an account owner.';
        } else if (errorMessage.includes('Only owners')) {
          userMessage = 'Only owners can perform this action.';
        } else {
          userMessage = 'Unable to send invitation. Please try again.';
        }

        const e = new Error(userMessage);
        log.error(`Error inviting user: ${errorMessage}`, e);
        throw e;
      }),
  );

/**
 * Resend invite email to an already-invited User
 */
export const resendUserAccountInvite = (userId: string, inviteMemo: string | null): Promise<void> =>
  getSession().then((session) =>
    fetch(urlWithAdminQuery(`/account/resendUserAccountInvite/${userId}`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
      body: JSON.stringify({ invite_memo: inviteMemo || '' }),
    })
      .then((resp) => {
        if (!resp.ok) {
          // still throw on non-2xx
          return resp.text().then((t) => {
            throw new Error(t);
          });
        }
        // success but no JSON: resolve void
        return;
      })
      .catch((err) => {
        const errorMessage = err.message || '';
        let userMessage = '';

        if (errorMessage.includes('Only owners can resend invites to other owners')) {
          userMessage = 'Only owners can resend invites to other owners.';
        } else if (errorMessage.includes('Only owners')) {
          userMessage = 'Only owners can perform this action.';
        } else {
          userMessage = errorMessage;
        }

        const e = new Error(userMessage);
        log.error(`Error resending invite: ${errorMessage}`, e);
        throw e;
      }),
  );

export const updateUserAccount = (
  userId: string,
  body: UserAccountUpdateRequest,
): Promise<IUpdateUserAccountNoteResponse> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/userAccount/${userId}`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
      body: JSON.stringify(body),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const errorMessage = err.message || '';
        let userMessage = '';

        if (errorMessage.includes('Only owners can modify other owners')) {
          userMessage = 'Only owners can modify other owners.';
        } else if (
          errorMessage.includes('Cannot modify user with role') &&
          errorMessage.includes('owner')
        ) {
          userMessage = 'Only owners can modify other owners.';
        } else if (errorMessage.includes('Only owners')) {
          userMessage = 'Only owners can perform this action.';
        } else if (errorMessage.includes('insufficient privileges')) {
          userMessage = "You don't have permission to modify this user.";
        } else {
          userMessage = errorMessage; // Don't wrap - let caller add context if needed
        }

        const e = new Error(userMessage);
        log.error(e);
        throw e;
      }),
  );
};

/**
 * Get a specific user account with notification settings by join record ID
 */
export const getUserAccount = (joinRecordId: string): Promise<IUserAccountBase> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery(`/userAccount/${joinRecordId}`, session.adminEmail);

    return fetch(url, {
      method: 'GET',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const e = new Error(`Error getting user account: ${err.message}`);
        toast.error(e.message, { closeButton: true });
        log.error(e);
        throw e;
      });
  });
};

/**
 * Update a user account with notification settings by join record ID
 */
export const updateUserAccountSettings = (
  joinRecordId: string,
  userAccount: IUserAccountBase,
): Promise<IUserAccountBase> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery(`/userAccount/${joinRecordId}`, session.adminEmail);

    // Import the serializer function
    const { serializeUserAccountUpdate } = require('@/api/serializers/userAccount');

    // Use the serializer to ensure schema compliance
    const requestBody = serializeUserAccountUpdate({
      note: userAccount.note,
      emailFlags: userAccount.enabled_email_notifications,
      smsFlags: userAccount.enabled_sms_notifications,
    });

    return fetch(url, {
      method: 'PUT',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
      body: JSON.stringify(requestBody),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const errorMessage = err.message || '';
        let userMessage = '';

        if (errorMessage.includes('Only owners can modify other owners')) {
          userMessage = 'Only owners can modify other owners.';
        } else if (errorMessage.includes('Only owners')) {
          userMessage = 'Only owners can perform this action.';
        } else {
          userMessage = errorMessage;
        }

        const e = new Error(userMessage);
        log.error(`Error updating user account settings: ${errorMessage}`, e);
        throw e;
      });
  });
};

/**
 * Update account-level summary settings
 */
export const updateAccount = (
  accountId: string,
  summarySettings: {
    timezone_key?: string;
    meter_health_summary_enabled?: boolean;
    meter_health_summary_day_of_week?: number;
    meter_health_summary_hour?: number;
  },
): Promise<IAccountWithUserAccounts> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery(`/account/${accountId}`, session.adminEmail);

    return fetch(url, {
      method: 'PUT',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
      body: JSON.stringify(summarySettings),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const e = new Error(`Error updating account settings: ${err.message}`);
        toast.error(e.message, { closeButton: true });
        log.error(e);
        throw e;
      });
  });
};

/**
 * List all available roles for RBAC
 */
export const listRoles = (): Promise<IListRolesResponse> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery('/roles', session.adminEmail);

    return fetch(url, {
      method: 'GET',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const e = new Error(`Error listing roles: ${err.message}`);
        toast.error(e.message, { closeButton: true });
        log.error(e);
        throw e;
      });
  });
};

/**
 * Get the current user's permissions
 */
export const getMyPermissions = (): Promise<IMyPermissionsResponse> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery('/me/permissions', session.adminEmail);

    return fetch(url, {
      method: 'GET',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const e = new Error(`Error getting permissions: ${err.message}`);
        toast.error(e.message, { closeButton: true });
        log.error(e);
        throw e;
      });
  });
};

/**
 * Set a team member's role
 * Note: This function does NOT show toasts - the calling component handles error display
 */
export const setMemberRole = (userId: string, roleKey: string): Promise<IUserAccountBase> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery(`/account/members/${userId}/role`, session.adminEmail);

    const body: ISetMemberRoleRequest = { role_key: roleKey };

    return fetch(url, {
      method: 'PATCH',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
      body: JSON.stringify(body),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const errorMessage = err.message || '';
        log.error(new Error(`Error setting member role: ${errorMessage}`));
        // Re-throw with original message so component can handle display
        throw err;
      });
  });
};

/**
 * Delete a team member from the account
 */
export const deleteUser = (userId: string): Promise<IAccountWithUserAccounts> => {
  return getSession().then((session) => {
    const url = urlWithAdminQuery(`/account/members/${userId}`, session.adminEmail);

    return fetch(url, {
      method: 'DELETE',
      headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
    })
      .then(getJSONOrThrow)
      .catch((err) => {
        const errorMessage = err.message || '';
        let userMessage = '';

        if (errorMessage.includes('Only owners can delete other owners')) {
          userMessage = 'Only owners can delete other owners.';
        } else if (errorMessage.includes('Cannot delete the last owner')) {
          userMessage = 'Cannot delete the last owner. Assign another owner first.';
        } else if (errorMessage.includes('Billing managers cannot delete team members')) {
          userMessage = 'Billing managers cannot delete team members.';
        } else if (errorMessage.includes('Only owners')) {
          userMessage = 'Only owners can perform this action.';
        } else {
          userMessage = `Error deleting team member: ${errorMessage}`;
        }

        const e = new Error(userMessage);
        // Don't show toast here - let the caller handle it to avoid duplicate toasts
        log.error(e);
        throw e;
      });
  });
};
